import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogComponent } from '@ams/ui-kit';
import { LegalAcceptanceGateService } from '../../services/legal-acceptance-gate.service';

/**
 * Root-mounted, non-dismissible gate shown when the current user's accepted
 * Terms/Privacy version is stale (Membora or their tenant replaced the
 * document since they last agreed). Only shows checkboxes for whichever of
 * Terms/Privacy is actually outstanding.
 */
@Component({
  selector: 'app-legal-acceptance-gate',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogComponent],
  templateUrl: './legal-acceptance-gate.component.html',
  styleUrl: './legal-acceptance-gate.component.scss',
})
export class LegalAcceptanceGateComponent {
  readonly gate = inject(LegalAcceptanceGateService);

  readonly agreeTerms = signal(false);
  readonly agreePrivacy = signal(false);
  readonly submitting = signal(false);
  readonly errorMessage = signal('');

  // The template binds [open] instead of wrapping the dialog in @if, so these
  // read through a possibly-null status rather than an @if-narrowed one.
  readonly isOpen = computed(() => this.gate.status() !== null);
  readonly needsTerms = computed(() => this.gate.status()?.needsAcceptTerms ?? false);
  readonly needsPrivacy = computed(() => this.gate.status()?.needsAcceptPrivacy ?? false);
  readonly termsUrl = computed(() => this.gate.status()?.termsUrl ?? '');
  readonly privacyUrl = computed(() => this.gate.status()?.privacyUrl ?? '');

  readonly canContinue = computed(() => {
    const status = this.gate.status();
    if (!status) return false;
    if (status.needsAcceptTerms && !this.agreeTerms()) return false;
    if (status.needsAcceptPrivacy && !this.agreePrivacy()) return false;
    return true;
  });

  async onContinue(): Promise<void> {
    const status = this.gate.status();
    if (!status || !this.canContinue()) return;

    this.submitting.set(true);
    this.errorMessage.set('');
    try {
      await this.gate.accept(
        status.needsAcceptTerms ? this.agreeTerms() : true,
        status.needsAcceptPrivacy ? this.agreePrivacy() : true,
      );
      this.agreeTerms.set(false);
      this.agreePrivacy.set(false);
    } catch (err: unknown) {
      const httpError = err as { error?: { error?: string } };
      this.errorMessage.set(httpError?.error?.error ?? 'Failed to save your acceptance. Please try again.');
    } finally {
      this.submitting.set(false);
    }
  }
}
